import { assert } from '@/utils/assert'

import { calculateMaxBorrowBasedOnCollateral } from '../action-max-value-getters/calculateMaxBorrowBasedOnCollateral'
import { MarketInfo, Reserve, UserPositionSummary } from '../market-info/marketInfo'
import { ReserveStatus } from '../market-info/reserve-status'
import { CheckedAddress } from '../types/CheckedAddress'
import { NormalizedUnitNumber } from '../types/NumericValues'
import { getBorrowableAmount } from '@/utils/getBorrowableAmount'
import { TokenSymbol } from '../types/TokenSymbol'

export interface ValidateBorrowParams {
  value: NormalizedUnitNumber

  asset: {
    address: CheckedAddress
    symbol: TokenSymbol
    status: ReserveStatus
    borrowingEnabled: boolean
    availableLiquidity: NormalizedUnitNumber
    totalDebt: NormalizedUnitNumber
    borrowCap?: NormalizedUnitNumber
    isSiloed: boolean
    borrowableInIsolation: boolean
    eModeCategory: number
  }

  user: {
    maxBorrowBasedOnCollateral: NormalizedUnitNumber
    totalBorrowedUSD: NormalizedUnitNumber
    isInSiloMode: boolean
    siloModeAsset?: CheckedAddress
    inIsolationMode: boolean
    isolationModeCollateralTotalDebt?: NormalizedUnitNumber
    isolationModeCollateralDebtCeiling?: NormalizedUnitNumber
    eModeCategory: number
  }

  pair?: {
    totalBorrowed: NormalizedUnitNumber
    borrowLimit?: NormalizedUnitNumber
  }
}

export type BorrowValidationIssue =
  | 'value-not-positive'
  | 'reserve-not-active'
  | 'reserve-borrowing-disabled'
  | 'exceeds-liquidity'
  | 'exceeds-supply-cap'
  | 'borrow-cap-reached'
  | 'insufficient-collateral'
  | 'siloed-mode-cannot-enable'
  | 'siloed-mode-enabled'
  | 'asset-not-borrowable-in-isolation'
  | 'isolation-mode-debt-ceiling-exceeded'
  | 'emode-category-mismatch'
  | 'exceeds-borrow-limit'

export function validateBorrow({
  value,
  asset: {
    address,
    symbol,
    status,
    borrowingEnabled,
    availableLiquidity,
    totalDebt,
    borrowCap,
    isSiloed,
    borrowableInIsolation,
    eModeCategory: assetEModeCategory,
  },
  user: {
    maxBorrowBasedOnCollateral,
    totalBorrowedUSD,
    isInSiloMode,
    siloModeAsset,
    inIsolationMode,
    isolationModeCollateralTotalDebt,
    isolationModeCollateralDebtCeiling,
    eModeCategory: userEModeCategory,
  },
  pair,
}: ValidateBorrowParams): BorrowValidationIssue | undefined {
  const borrowedAnythingBefore = !totalBorrowedUSD.isEqualTo(0)

  if (value.isLessThanOrEqualTo(0)) {
    return 'value-not-positive'
  }

  if (status !== 'active') {
    return 'reserve-not-active'
  }

  if (!borrowingEnabled) {
    return 'reserve-borrowing-disabled'
  }

  if (availableLiquidity.lt(value) && symbol === 'USDXL') {
    return 'exceeds-supply-cap'
  }

  if (availableLiquidity.lt(value)) {
    return 'exceeds-liquidity'
  }

  if (borrowCap?.lt(totalDebt.plus(value))) {
    return 'borrow-cap-reached'
  }

  if (value.gt(maxBorrowBasedOnCollateral)) {
    return 'insufficient-collateral'
  }

  if (inIsolationMode) {
    if (!borrowableInIsolation) {
      return 'asset-not-borrowable-in-isolation'
    }

    assert(
      isolationModeCollateralTotalDebt && isolationModeCollateralDebtCeiling,
      'Collateral total debt, ceiling and decimals should be defined',
    )

    if (isolationModeCollateralTotalDebt.plus(value).gt(isolationModeCollateralDebtCeiling)) {
      return 'isolation-mode-debt-ceiling-exceeded'
    }
  }

  if (userEModeCategory !== 0 && userEModeCategory !== assetEModeCategory) {
    return 'emode-category-mismatch'
  }

  if (borrowedAnythingBefore) {
    if (isInSiloMode) {
      if (address !== siloModeAsset) {
        return 'siloed-mode-enabled'
      }
    } else {
      if (isSiloed) {
        return 'siloed-mode-cannot-enable'
      }
    }
  }

  if (pair) {
    if (typeof pair.borrowLimit !== 'undefined' && pair.totalBorrowed.plus(value).gt(pair.borrowLimit)) {
      return 'exceeds-borrow-limit'
    }
  }
}

export const borrowValidationIssueToMessage: Record<BorrowValidationIssue, string> = {
  'value-not-positive': 'Borrow value should be positive',
  'exceeds-supply-cap': 'Borrow value exceeds supply cap',
  'exceeds-liquidity': 'Borrow value exceeds liquidity',
  'borrow-cap-reached': 'Borrow cap reached',
  'insufficient-collateral': 'Not enough collateral to borrow this amount',
  'siloed-mode-enabled': 'Siloed borrowing enabled. Borrowing other assets is not allowed.',
  'siloed-mode-cannot-enable':
    "Borrowing asset is siloed. Can't add it to position with other assets already borrowed.",
  'reserve-not-active': 'Borrowing is not available for this asset',
  'reserve-borrowing-disabled': 'Borrowing is not available for this asset',
  'asset-not-borrowable-in-isolation': 'Borrowing is not available for this asset in isolation mode',
  'isolation-mode-debt-ceiling-exceeded': 'Borrowing exceeds isolation mode debt ceiling',
  'emode-category-mismatch': 'Asset and user eMode categories do not match',
  'exceeds-borrow-limit': "Value exceeds the pair's borrow limit",
}

export function getValidateBorrowArgs(
  value: NormalizedUnitNumber,
  reserve: Reserve,
  marketInfo: MarketInfo,
  _userSummary?: UserPositionSummary,
): ValidateBorrowParams {
  const userSummary = _userSummary ?? marketInfo.userPositionSummary
  const availableLiquidity = getBorrowableAmount({
    tokenIdentifier: reserve.token.symbol,
    facilitatorAvailable: marketInfo.facilitator.availableToBorrow,
    defaultAvailable: reserve.availableLiquidity,
  })

  return {
    value,
    asset: {
      address: reserve.token.address,
      symbol: reserve.token.symbol,
      status: reserve.status,
      borrowingEnabled: reserve.borrowEligibilityStatus !== 'no',
      availableLiquidity,
      totalDebt: reserve.totalDebt,
      borrowCap: reserve.borrowCap,
      isSiloed: reserve.isSiloedBorrowing,
      borrowableInIsolation: reserve.isBorrowableInIsolation,
      eModeCategory: reserve.eModeCategory?.id ?? 0,
    },
    user: {
      maxBorrowBasedOnCollateral: calculateMaxBorrowBasedOnCollateral({
        borrowingAssetPriceUsd: reserve.token.unitPriceUsd,
        totalCollateralUSD: userSummary.totalCollateralUSD,
        maxLoanToValue: userSummary.maxLoanToValue,
        totalBorrowsUSD: marketInfo.userPositionSummary.totalBorrowsUSD,
      }),
      totalBorrowedUSD: userSummary.totalBorrowsUSD,
      isInSiloMode: marketInfo.userConfiguration.siloBorrowingState.enabled,
      siloModeAsset: marketInfo.userConfiguration.siloBorrowingState.enabled
        ? marketInfo.userConfiguration.siloBorrowingState.siloedBorrowingReserve.token.address
        : undefined,
      inIsolationMode: marketInfo.userConfiguration.isolationModeState.enabled,
      isolationModeCollateralTotalDebt: marketInfo.userConfiguration.isolationModeState.enabled
        ? marketInfo.userConfiguration.isolationModeState.isolatedBorrowingReserve.isolationModeTotalDebt
        : undefined,
      isolationModeCollateralDebtCeiling: marketInfo.userConfiguration.isolationModeState.enabled
        ? marketInfo.userConfiguration.isolationModeState.isolatedBorrowingReserve.debtCeiling
        : undefined,
      eModeCategory: marketInfo.userConfiguration.eModeState.enabled
        ? marketInfo.userConfiguration.eModeState.category.id
        : 0,
    },
  }
}
